import { Text, View, StyleSheet } from '@react-pdf/renderer';
import { sharedStyles, pdfColors } from './sharedStyles';
import RadioOptions from './RadioOptions';
import ContentRenderer from './ContentRenderer';
import type { AssessmentReportQuestion } from './types';

const styles = StyleSheet.create({
  questionItem: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: pdfColors.border,
  },
  lastQuestionItem: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  questionText: {
    fontSize: 10,
    color: pdfColors.textLight,
    lineHeight: 1.5,
    marginBottom: 6,
  },
});

interface QuestionGroup {
  id: string;
  title: string;
  position: number;
  questions: AssessmentReportQuestion[];
}

interface QuestionGroupItemProps {
  questionGroup: QuestionGroup;
  answerIds: string[];
  correct: boolean;
}

function QuestionGroupItem({
  questionGroup,
  answerIds,
  correct,
}: QuestionGroupItemProps): React.ReactElement {
  return (
    <View style={sharedStyles.itemContainer}>
      <View style={sharedStyles.itemHeader}>
        <Text style={sharedStyles.itemTitle}>
          {questionGroup.position}. {questionGroup.title}
        </Text>
        <View style={correct ? sharedStyles.resultBadgeCorrect : sharedStyles.resultBadgeIncorrect}>
          <Text style={sharedStyles.resultBadgeText}>{correct ? 'CORRECT' : 'INCORRECT'}</Text>
        </View>
      </View>

      {questionGroup.questions.map((question, questionIndex) => {
        const isLastQuestion = questionIndex === questionGroup.questions.length - 1;
        const selectedOption = question.options.find((opt) => answerIds.includes(opt.id));
        const selectedIdsForThisQuestion = selectedOption != null ? [selectedOption.id] : [];

        return (
          <View
            key={question.id}
            style={isLastQuestion ? styles.lastQuestionItem : styles.questionItem}
          >
            {question.questionContentFormatted != null &&
            question.questionContentFormatted.length > 0 ? (
              <ContentRenderer content={question.questionContentFormatted} />
            ) : null}

            <View wrap={false}>
              <RadioOptions
                options={question.options.map((opt) => ({
                  id: opt.id,
                  label: opt.answer,
                  isCorrect: opt.isCorrect,
                }))}
                selectedIds={selectedIdsForThisQuestion}
              />
            </View>

            {selectedOption != null && selectedOption.feedback.length > 0 ? (
              <View style={sharedStyles.feedbackSection} wrap={false}>
                <Text style={sharedStyles.feedbackLabel}>ASSESSOR FEEDBACK</Text>
                <Text style={sharedStyles.feedbackText}>{selectedOption.feedback}</Text>
              </View>
            ) : null}
          </View>
        );
      })}
    </View>
  );
}

export default QuestionGroupItem;
